/**
 * OC Default Theme
 * Default theme values matching online-community's actual branding
 * Used in ThemeProvider to provide OC styling to the widget
 * 
 * This theme extends the base themes from Theme.ts and only overrides
 * properties specific to the OC widget context.
 */

import { IAppTheme } from '../../../styles/IAppTheme';
import { appTheme } from '../../../styles/Theme';

/**
 * OC-specific theme extension with additional colors for OC widget components
 */
export interface IOcAppTheme extends IAppTheme {
  color: IAppTheme['color'] & {
    /** Header background color */
    headerBg: string;
    /** Sources panel header text color ("Sources (X)") */
    sourcesHeaderCountText: string;
    /** Source card text color (title, description) */
    sourcesText: string;
    /** Footer disclaimer text color */
    footerText: string;
    /** Ask Human prompt text color ("Need opinions? AI can help you post.") */
    askHumanPromptText: string;
    /** Ask Human background color */
    askHumanBg: string;
    /** Ask Human button background color */
    askHumanButtonBg: string;
    /** Ask Human button text color */
    askHumanButtonText: string;
    /** Ask Human form label color */
    askHumanFormLabel?: string;
    /** Skeleton gradient light color (start/stop color) */
    loadingBackgroundGradientLight: string;
    /** Skeleton gradient dark color (start/stop color) */
    loadingBackgroundGradientDark: string;
  };
}

// Get the base themes from Theme.ts
const baseThemes = appTheme();

/**
 * Default OC theme - matches online-community branding
 * Extends the base light theme and overrides OC-specific values
 */
export const OC_DEFAULT_THEME: IOcAppTheme = {
  ...baseThemes.light,
  color: {
    ...baseThemes.light.color,
    primary: '#007bff',
    betaBadgeBG: '#efefef',
        reaction: '#212529',            // --oc-text-primary-color
    reactionActive: '#0056b3',
    // OC-specific colors (not in base theme)
    headerBg: '#ffffff',           // --oc-bg0-color (white background)
    sourcesHeaderCountText: '#ffffff',   // --oc-text-primary-color (dark text on white background)
    sourcesText: '#212529',         // --oc-text-primary-color (source card text)
    footerText: '#ffffff',          // --oc-text-secondary-color (muted disclaimer text)
    askHumanPromptText: '#6c757d',  // --oc-text-secondary-color (gray prompt text)
    askHumanBg: '#efefef',    // Light gray button background
    askHumanButtonBg: '#007bff',    // Light gray button background
    askHumanButtonText: '#212529',  // --oc-text-primary-color (button text)
    loadingBackgroundGradientLight: 'rgba(240, 240, 240, 0.25)',  // Light gradient color for skeleton animation
    loadingBackgroundGradientDark: 'rgba(200, 200, 200, 0.2)',    // Dark gradient color for skeleton animation
  },
};

/**
 * Dark mode OC theme
 * Extends the base dark theme and overrides OC-specific values
 */
export const OC_DARK_THEME: IOcAppTheme = {
  ...baseThemes.dark,
  
  color: {
    ...baseThemes.dark.color,
    primary: '#4c4c4c',
    chatHeading: '#E8E8E8',             // Dark background for header
    userMessageBG: '#f58026',
    
    // OC-specific dark mode colors (not in base theme)
    headerBg: '#4c4c4c',             // Dark background for header
    sourcesHeaderCountText: '#ffffff',    // Light text on dark background
    sourcesText: '#E8E8E8',          // Light source card text
    footerText: '#adb5bd',           // Lighter gray for muted text
    askHumanPromptText: '#f58026',   // Lighter gray prompt text
    askHumanBg: '#3a3a3a',     // Darker button background
    askHumanButtonBg: '#f58026',
    askHumanButtonText: '#ffffff',   // Light button text
    loadingBackgroundGradientLight: 'rgba(60, 60, 60, 0.3)',   // Light gradient for dark mode skeleton
    loadingBackgroundGradientDark: 'rgba(40, 40, 40, 0.25)',   // Dark gradient for dark mode skeleton
  },
};

